import { useState } from 'react';
import { X } from 'lucide-react';
import CommercialTermsFields, {
  termsPayload,
  type CommercialTerms,
} from './CommercialTermsFields';
import type { Client, ClientLifecycle } from '../types';
import { followAgreement } from '../lib/requirementOptions';

/**
 * Correcting a client that already exists.
 *
 * Until this there was no way to change one at all. The tax fields, the
 * escalation point and the GSTIN were only on the create form, and every
 * client in the live data arrived through the importer instead — so the GST
 * and TDS rates invoicing depends on could not be set on a single real client,
 * and every "Ready to invoice" row read "Tax on file: None set" with no way to
 * fix it.
 *
 * Name is editable, and the route keeps the old spelling as an alias so the
 * importer keeps resolving it — which is the half that was missing when this
 * was locked. Renaming onto a name another client already holds is refused
 * and points at the merge panel, because joining two clients is what somebody
 * wants there and a rename cannot do it.
 *
 * Legal name is the field most people reaching for a rename actually wanted:
 * "AHC Healthcare Services Private Limited" on the invoice, "AHC" everywhere
 * else. Blank means the short name is the legal one, which is true of most
 * clients.
 *
 * The server refuses this to anyone below company admin regardless of who can
 * see the button. Hiding the control is courtesy; the 403 behind it is the
 * rule.
 */
export default function ClientEditDialog({
  client,
  saving,
  onCancel,
  onSave,
}: {
  client: Client;
  saving: boolean;
  onCancel: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(client.name);
  const [legalName, setLegalName] = useState(client.legalName ?? '');
  const [gstin, setGstin] = useState(client.gstin ?? '');
  const [gstRate, setGstRate] = useState(client.gstRate ?? '');
  const [tdsRate, setTdsRate] = useState(client.tdsRate ?? '');
  const [tdsSection, setTdsSection] = useState(client.tdsSection ?? '');
  const [address, setAddress] = useState(client.address ?? '');
  const [city, setCity] = useState(client.city ?? '');
  const [state, setState] = useState(client.state ?? '');
  const [industry, setIndustry] = useState(client.industry ?? '');
  const [lifecycle, setLifecycle] = useState<ClientLifecycle>(client.lifecycle);
  const [contactName, setContactName] = useState(client.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(client.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(client.contactPhone ?? '');
  // Seeded from the row so the dialog opens showing what was agreed. An
  // unset basis stays unset — the dialog must not turn "no standing terms"
  // into a choice nobody made just by being opened and saved.
  const [terms, setTerms] = useState<CommercialTerms>({
    agreementState: client.agreementState ?? '',
    feeBasis: client.defaultFeeBasis ?? '',
    feePercent: client.defaultFeePercent ?? '',
    flatAmount: client.defaultBillingAmount ?? '',
    paymentTerms: String(client.paymentTermsDays),
    replacementDays:
      client.defaultReplacementDays === null
      || client.defaultReplacementDays === undefined
        ? ''
        : String(client.defaultReplacementDays),
  });
  const [website, setWebsite] = useState(client.website ?? '');

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <form
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({
            name: name.trim(),
            // Null, not an empty string: null means "the short name is the
            // legal one", and the server falls back to it when invoicing.
            legalName: legalName.trim() || null,
            gstin: gstin.trim() || null,
            // Null, not zero, when blank. The server reads null as "not
            // established" and applies nothing; zero would read as a decision.
            gstRate: gstRate.trim() || null,
            tdsRate: tdsRate.trim() || null,
            tdsSection: tdsSection.trim() || null,
            address: address.trim() || null,
            city: city.trim() || null,
            state: state.trim() || null,
            industry: industry.trim() || null,
            lifecycle,
            contactName: contactName.trim() || null,
            contactEmail: contactEmail.trim() || null,
            contactPhone: contactPhone.trim() || null,
            ...termsPayload(terms),
            website: website.trim() || null,
          });
        }}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Edit {client.name}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Renaming keeps the old spelling working, so imports that used it
              still match. To join two clients into one, use Merge on the
              duplicates panel — a rename cannot do it.
            </p>
          </div>
          <button type="button" className="btn-ghost" onClick={onCancel} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Name
            </h4>
            <div>
              <label className="label" htmlFor="ce-name">Client Name</label>
              <input
                spellCheck={false}
                id="ce-name"
                className="input"
                maxLength={160}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                {/* The short spelling everything matches on: the importer's
                    aliases, every dropdown, every screen. The registered name
                    is a different field and lives under Tax Treatment, beside
                    the GSTIN it is taken from. */}
                What everyone types and every sheet matches on. The old
                spelling keeps working after a rename.
              </p>
            </div>
          </section>

          {/* Agreed once with the client, suggested on every placement.
              Same control set the add form uses, so the two cannot drift. */}
          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Commercial Terms
            </h4>
            <CommercialTermsFields
              value={terms}
              onChange={(next) => {
                setTerms(next);
                setLifecycle(followAgreement(next.agreementState, lifecycle));
              }}
            />
          </section>

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Tax Treatment
            </h4>
            {/* Beside the GSTIN rather than beside the client name, because
                it is the name *on* that registration. Somebody filling this in
                is reading it off the same certificate. */}
            <div className="mb-3">
              <label className="label" htmlFor="ce-legal-name">Company Name</label>
              <input
                spellCheck={false}
                id="ce-legal-name"
                className="input"
                maxLength={200}
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Name as per the GSTIN"
              />
              <p className="mt-1 text-xs text-gray-500">
                Printed on invoices raised from now on. Leave blank if the
                client name is already the registered one.
              </p>
            </div>

            <div>
              <label className="label" htmlFor="ce-gstin">GSTIN</label>
              <input
                spellCheck={false}
                id="ce-gstin"
                /* Spaced, because a GSTIN is read a character at a time
                   against a certificate rather than as a word. The rest of
                   the form keeps the ordinary spacing. */
                className="input tracking-[0.12em]"
                maxLength={15}
                value={gstin}
                onChange={(e) => setGstin(e.target.value)}
                /* 06 is Haryana, matching the State placeholder below and the
                   pair on the add form. It read 36 -- Telangana -- beside a
                   Haryana example, which is a worked example that contradicts
                   itself on the one field where the first two digits decide
                   which tax an invoice carries. */
                placeholder="06AAFCD7691C2ZM"
              />
              <p className="mt-1 text-xs text-gray-500">
                {/* Two different things wearing similar names, and confusing
                    them puts the wrong one on an invoice. */}
                Their registration number, printed on their invoices. Not the
                same as the GST rate below. Fifteen characters.
              </p>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <div>
                <label className="label" htmlFor="ce-gst">GST %</label>
                <input
                  spellCheck={false}
                  id="ce-gst"
                  className="input"
                  inputMode="decimal"
                  value={gstRate}
                  onChange={(e) => setGstRate(e.target.value)}
                  placeholder="18"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-tds">TDS %</label>
                <input
                  spellCheck={false}
                  id="ce-tds"
                  className="input"
                  inputMode="decimal"
                  value={tdsRate}
                  onChange={(e) => setTdsRate(e.target.value)}
                  placeholder="10"
                />
              </div>
            </div>

            <label className="label mt-3" htmlFor="ce-section">TDS Section</label>
            <input
              spellCheck={false}
              id="ce-section"
              className="input"
              maxLength={20}
              value={tdsSection}
              onChange={(e) => setTdsSection(e.target.value)}
              placeholder="194J"
            />

          </section>

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Contact
            </h4>
            <div className="mb-3">
              <label className="label" htmlFor="ce-address">
                Head Office Address
              </label>
              <textarea
                spellCheck={false}
                id="ce-address"
                className="input"
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="As on the GST certificate — building, street, area, landmark"
              />
              <p className="mt-0.5 text-[11px] text-gray-400">
                The one that goes on their invoices. Other locations are on the
                client&rsquo;s own page.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                {/* Was labelled "Client Address (Head Office Or Billing)"
                    over the 100-character city column -- a label promising an
                    address the schema did not have. Both exist now. */}
                <label className="label" htmlFor="ce-city">City</label>
                <input
                  spellCheck={false}
                  id="ce-city"
                  className="input"
                  maxLength={100}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Gurugram"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-state">State</label>
                <input
                  spellCheck={false}
                  id="ce-state"
                  className="input"
                  maxLength={100}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                placeholder="Haryana"
                />
                {/* Not cosmetic, and the reason this field being absent from
                    the edit dialog mattered: an imported client has no state,
                    and place of supply is what decides the split. */}
                <p className="mt-1 text-xs text-gray-500">
                  Decides whether their invoices carry IGST or CGST + SGST.
                </p>
              </div>
              <div>
                <label className="label" htmlFor="ce-industry">Industry</label>
                <input
                  spellCheck={false}
                  id="ce-industry"
                  className="input"
                  maxLength={120}
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                placeholder="Healthcare"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-contact">Client Contact Name</label>
                <input
                  spellCheck={false}
                  id="ce-contact"
                  className="input"
                  maxLength={160}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                placeholder="Priya Menon"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-email">Contact Email</label>
                <input
                  spellCheck={false}
                  id="ce-email"
                  type="email"
                  className="input"
                  maxLength={255}
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                placeholder="priya@example.com"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-phone">Contact Phone</label>
                <input
                  spellCheck={false}
                  id="ce-phone"
                  className="input"
                  inputMode="tel"
                  maxLength={20}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                placeholder="9876543210"
                />
              </div>
              <div>
                <label className="label" htmlFor="ce-web">Website</label>
                <input
                  spellCheck={false}
                  id="ce-web"
                  type="url"
                  className="input"
                  maxLength={255}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                />
              </div>
            </div>
          </section>

          {/* The escalation point is a list now, and it lives on the
              client's own page beside the interview points. Editing it
              here as well would be a second editor for the same rows,
              which is how two views of one record come to disagree. */}

          <section>
            <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </h4>
            {/* The field is called Status now, matching the column on the
                Clients page this dialog opens from -- one field called two
                things across two screens is how somebody concludes they are
                two fields.

                Which leaves it sharing a name with the section heading above,
                since this section holds nothing else. The heading stays, so
                the dialog keeps the five-section rhythm its other headings
                set, and the label goes sr-only rather than being deleted: the
                select still needs an accessible name, and "Status Status" is
                only redundant to somebody who can see both. */}
            <label className="sr-only" htmlFor="ce-lifecycle">Status</label>
            <select
              id="ce-lifecycle"
              className="input"
              value={lifecycle}
              onChange={(e) => setLifecycle(e.target.value as ClientLifecycle)}
            >
              {/* Three, and no "Discussion going on": that is an agreement
                  answer, and this follows from it — see followAgreement. */}
              <option value="pipeline">Pipeline</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive — Dormant Or Ended</option>
            </select>
            <div className="mt-1 text-xs text-gray-600">
              <span>
                {/* Hiding, not removing. Nothing in the application reads this
                    flag except the client list's own filter -- submissions,
                    requirements, placements and invoices all work exactly the
                    same against an inactive client, and the existing rows are
                    untouched. It is the answer for a client nobody is working
                    any more, where a delete would refuse and should. */}
                {/* The asymmetry worth stating on screen, because it is not
                    guessable: the two pickers want opposite tails. */}
                <span className="mt-0.5 block text-[11px] text-gray-400">
                  A client in the pipeline can be called and sourced against
                  but not invoiced.
                  An inactive client can still be invoiced — a final invoice
                  arrives after the work ends — but is offered for no new work.
                  Either way it stays on every record already naming it.
                </span>
              </span>
            </div>
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
